import { useEffect, useState } from 'react';
import {
  api,
  ErrorApi,
  type MotivoAnulacion,
  type Partido,
  type Perfil,
  type Yo,
} from '../services/api';
import { Aviso, Boton, Cargando, Tarjeta, Titulo, Vacio } from '../components/ui';
import { MarcadorPartido } from '../components/MarcadorPartido';
import { CorregirMarcador } from '../components/CorregirMarcador';
import { diasEntre, fechaHora, hoy, plazoAutoconfirmar } from '../nucleo/fechas';
import { PlacaNoche } from '../components/PlacaNoche';
import { CartelMes } from '../components/CartelMes';
import { TarjetaProgreso } from '../components/TarjetaProgreso';

/**
 * Inicio: lo pendiente arriba de todo.
 *
 * Es la decision de producto mas importante de la pantalla. La falla mas
 * probable de esta app no es tecnica: es que se carguen partidos, nadie
 * confirme, la tabla se congele y en dos semanas no entre nadie mas. Por eso
 * lo que espera confirmacion no esta en una solapa: es lo primero que se ve.
 */
export function Inicio({
  yo,
  partidos,
  cargando,
  onCambio,
  onIrACargar,
  onVerPerfil,
  onVerPartido,
  onVerHistorial,
  onVerResumen,
  onVerMes,
}: {
  yo: Yo;
  partidos: { confirmados: Partido[]; pendientes: Partido[] } | null;
  cargando: boolean;
  onCambio: () => Promise<void>;
  onIrACargar: () => void;
  onVerPerfil: (id: number) => void;
  onVerPartido: (id: number) => void;
  onVerHistorial: () => void;
  onVerResumen: (mes: string) => void;
  onVerMes: () => void;
}) {
  const [ocupado, setOcupado] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // Anular pide confirmacion en el lugar: rehace el ranking de cuatro personas.
  const [porAnular, setPorAnular] = useState<number | null>(null);
  // Confirmar como admin tambien pide el segundo toque: cuenta para cuatro.
  const [porConfirmar, setPorConfirmar] = useState<number | null>(null);
  // Rechazar tambien: con los botones grandes es facil tocarlo sin querer.
  const [porRechazar, setPorRechazar] = useState<number | null>(null);
  // El pendiente cuyo marcador se esta corrigiendo (el que lo cargo o su compañero).
  const [corrigiendo, setCorrigiendo] = useState<number | null>(null);
  const [viendoPlaca, setViendoPlaca] = useState(false);
  // La tarjeta de los ultimos partidos, abierta o plegada. null = lo decide
  // la fecha: abierta si la ultima noche es de hoy o de ayer.
  const [abiertosElegido, setAbiertos] = useState<boolean | null>(null);
  // El perfil propio, para que la tarjeta de "Mi perfil" muestre que hay
  // adentro (puesto, racha, logros) en vez de pedir el toque a ciegas. Se
  // vuelve a pedir cuando cambian los partidos: confirmar uno puede dar un logro.
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [errorPerfil, setErrorPerfil] = useState(false);
  const [reintentoPerfil, setReintentoPerfil] = useState(0);
  useEffect(() => {
    let vigente = true;
    setErrorPerfil(false);
    api
      .perfil(yo.jugador.id)
      .then((p) => vigente && setPerfil(p))
      .catch(() => vigente && setErrorPerfil(true));
    return () => {
      vigente = false;
    };
  }, [yo.jugador.id, partidos, reintentoPerfil]);

  if (cargando && !partidos) return <Cargando que="Buscando partidos" />;
  if (!partidos) return <Aviso tono="error">No se pudieron traer los partidos.</Aviso>;

  const mios = partidos.pendientes.filter((p) => p.puedo_confirmar);

  // La placa se ofrece siempre para la ultima noche con partidos confirmados,
  // sea de cuando sea: antes solo si era de hoy o de ayer, y pasado eso habia
  // que ir a buscarla al historial (a Marcos le costo encontrarla). Las de
  // noches anteriores siguen en el historial, dia por dia.
  const noche = partidos.confirmados.find((p) => p.estado === 'confirmado')?.jugado_el ?? null;
  const abiertos = abiertosElegido ?? (noche !== null && diasEntre(noche, hoy()) <= 1);
  const esperandoOtros = partidos.pendientes.filter((p) => !p.puedo_confirmar);

  // "Ultimos partidos" es la ultima noche jugada, nada mas. Vienen del mas
  // nuevo al mas viejo, asi que el primero dice cual es.
  const ultimoDia = partidos.confirmados[0]?.jugado_el ?? null;
  const delDia = partidos.confirmados.filter((p) => p.jugado_el === ultimoDia);

  async function responder(id: number, acepta: boolean) {
    setOcupado(id);
    setError(null);
    setAviso(null);
    try {
      if (acepta) await api.confirmar(id);
      else await api.rechazar(id);
      setPorConfirmar(null);
      setPorRechazar(null);
      await onCambio();
      setAviso(acepta ? 'Partido confirmado. Ya no necesita confirmación del rival.' : 'Partido rechazado. Dejó de contar en las tablas.');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo.');
    } finally {
      setOcupado(null);
    }
  }

  async function anular(id: number, motivo: MotivoAnulacion) {
    setOcupado(id);
    setError(null);
    setAviso(null);
    try {
      await api.anular(id, motivo);
      setPorAnular(null);
      await onCambio();
      setAviso('Partido anulado. Dejó de contar en las tablas.');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo anular.');
    } finally {
      setOcupado(null);
    }
  }

  // El boton del renglon de la fecha, solo para el admin.
  function accionDeAdmin(partido: Partido) {
    if (partido.estado === 'anulado') {
      // Los anulados antes de que la app preguntara el motivo: un toque lo
      // completa. Con el motivo ya dicho no hay nada que hacer.
      if (partido.motivo_anulacion !== null) return null;
      return (
        <Boton
          mini
          tipo="secundario"
          onClick={() => anular(partido.id, 'duplicado')}
          deshabilitado={ocupado === partido.id}
        >
          Fue duplicado
        </Boton>
      );
    }
    if (porAnular === partido.id) return null;
    return (
      <Boton mini tipo="peligro" onClick={() => setPorAnular(partido.id)}>
        Anular
      </Boton>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {error && <Aviso tono="error">{error}</Aviso>}
      {aviso && <p role="status" className="rounded-xl border border-(--color-acento)/50 bg-(--color-panel) px-3 py-2 text-sm">{aviso}</p>}

      {/* La primera noche con la app, varios no entendieron que tenian que
          confirmar, o lo dejaron para despues y no vieron el partido en la
          tabla. Por eso el cartel es lo mas fuerte de la pantalla, dice en
          palabras que falta y por que importa, y la pregunta es directa. */}
      {mios.length > 0 && (
        <section className="flex flex-col gap-2">
          <div className="rounded-2xl bg-(--color-alerta) px-4 py-3 text-(--color-sobre-acento)">
            <p className="text-lg font-bold">
              {mios.length === 1
                ? 'Tenés un partido para confirmar'
                : `Tenés ${mios.length} partidos para confirmar`}
            </p>
            <p className="text-sm">
              Lo cargó alguien del equipo rival y ya cuenta en la tabla. Si está mal, rechazalo
              y sale.
            </p>
          </div>
          {mios.map((partido) => (
            <Tarjeta key={partido.id} compacta className="border-2 border-(--color-alerta)">
              <p className="font-semibold">¿El resultado está bien?</p>
              <MarcadorPartido
                partido={partido}
                yoId={yo.jugador.id}
                onAbrir={() => onVerPartido(partido.id)}
              />
              <p className="mt-1.5 text-xs text-(--color-apagado)">
                Lo cargó {partido.cargado_por_nombre}, el {fechaHora(partido.creado_en).dia} a
                las {fechaHora(partido.creado_en).hora}.
              </p>
              {porRechazar === partido.id ? (
                <div className="mt-3 flex flex-col gap-2">
                  <p className="text-sm">
                    Queda rechazado y no cuenta. Si el resultado fue otro, cárguenlo de nuevo.
                  </p>
                  <div className="flex gap-2">
                    <Boton
                      tipo="peligro"
                      ancho
                      onClick={() => responder(partido.id, false)}
                      deshabilitado={ocupado === partido.id}
                    >
                      Sí, rechazar
                    </Boton>
                    <Boton tipo="secundario" onClick={() => setPorRechazar(null)}>
                      Volver
                    </Boton>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <Boton
                    ancho
                    onClick={() => responder(partido.id, true)}
                    deshabilitado={ocupado === partido.id}
                  >
                    Sí, confirmar
                  </Boton>
                  {/* Sin esto el boton ancho de al lado lo aprieta a tres renglones. */}
                  <div className="shrink-0 whitespace-nowrap">
                    <Boton tipo="peligro" onClick={() => setPorRechazar(partido.id)}>
                      No, está mal
                    </Boton>
                  </div>
                </div>
              )}
            </Tarjeta>
          ))}
        </section>
      )}

      {/* Después de confirmar, que es lo urgente: el mes que cerró. */}
      <CartelMes yo={yo} onVerResumen={onVerResumen} />

      {mios.length === 0 && (
        <Tarjeta>
          <Titulo>Hola, {yo.jugador.nombre}</Titulo>
          <p className="mt-1 text-sm text-(--color-apagado)">No tenés nada pendiente.</p>
          <div className="mt-4">
            <Boton ancho onClick={onIrACargar}>
              Cargar un partido
            </Boton>
          </div>
        </Tarjeta>
      )}

      <TarjetaProgreso yo={yo} perfil={perfil} pendientes={partidos.pendientes}
        errorPerfil={errorPerfil} onReintentar={() => setReintentoPerfil(n => n + 1)}
        onPerfil={() => onVerPerfil(yo.jugador.id)} onMes={onVerMes} onResumen={onVerResumen} />

      {esperandoOtros.length > 0 && (
        <section className="flex flex-col gap-2">
          <Titulo chico>Esperando que confirme el rival</Titulo>
          <p className="-mt-1 text-xs text-(--color-apagado)">
            Si nadie lo confirma en {plazoAutoconfirmar()}, cuenta igual.
          </p>
          {esperandoOtros.map((partido) => (
            <Tarjeta key={partido.id} compacta>
              <div className="opacity-60">
                <MarcadorPartido
                  partido={partido}
                  yoId={yo.jugador.id}
                  accion={yo.jugador.rol === 'admin' && <Boton mini tipo="peligro" onClick={() => onVerPartido(partido.id)}>Anular</Boton>}
                  onAbrir={() => onVerPartido(partido.id)}
                />
              </div>

              {/* A quien le falta, y al lado el boton del admin: un solo
                  renglon. Confirmar como admin es para cuando los dos rivales
                  no usan la app. Fuera del periodo de prueba
                  (ADMIN_CONFIRMA_TODO en db.ts), el servidor no deja confirmar
                  como admin un partido cargado por uno mismo o por el
                  companero: ahi el flag no viene. */}
              {corrigiendo === partido.id ? (
                <CorregirMarcador
                  partido={partido}
                  onListo={() => {
                    setCorrigiendo(null);
                    onCambio();
                  }}
                  onCancelar={() => setCorrigiendo(null)}
                />
              ) : porConfirmar === partido.id ? (
                <div className="mt-1.5 flex items-center justify-end gap-2">
                  <span className="text-xs text-(--color-apagado)">¿Confirmar como admin?</span>
                  <Boton
                    chico
                    onClick={() => responder(partido.id, true)}
                    deshabilitado={ocupado === partido.id}
                  >
                    Sí
                  </Boton>
                  <Boton chico tipo="secundario" onClick={() => setPorConfirmar(null)}>
                    No
                  </Boton>
                </div>
              ) : (
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <p className="text-xs text-(--color-alerta)">
                    {quienFalta(partido, yo.jugador.id)}
                  </p>
                  {partido.puedo_corregir && (
                    <div className="shrink-0">
                      <Boton mini tipo="secundario" onClick={() => setCorrigiendo(partido.id)}>
                        Corregir
                      </Boton>
                    </div>
                  )}
                  {partido.puedo_confirmar_admin && (
                    <div className="shrink-0">
                      <Boton mini tipo="secundario" onClick={() => setPorConfirmar(partido.id)}>
                        Confirmar como admin
                      </Boton>
                    </div>
                  )}
                </div>
              )}
            </Tarjeta>
          ))}
        </section>
      )}

      {/* Solo la ultima noche: lo de antes esta en "Ver todos los partidos",
          que queda adentro de la tarjeta, al pie. Plegable como una tarjeta
          grande, al estilo de "Mi perfil". */}
      <div
        className="flex flex-col gap-3 overflow-hidden rounded-2xl border-2 border-(--color-acento)
          bg-linear-to-br from-(--color-heroe) to-(--color-panel) p-4 shadow-lg
          shadow-(--color-acento)/25"
      >
        <button
          onClick={() => setAbiertos(!abiertos)}
          aria-expanded={abiertos}
          className="flex items-center justify-between gap-3 text-left"
        >
          <span className="min-w-0">
            <span className="block font-titulo text-3xl">
              {ultimoDia === hoy() ? 'Partidos del día' : 'Últimos partidos'}
            </span>
            <span className="block text-sm text-(--color-apagado)">
              {ultimoDia
                ? `${delDia.length === 1 ? '1 partido' : `${delDia.length} partidos`} · ${cuando(ultimoDia)}`
                : 'Todavía no hay partidos'}
            </span>
          </span>
          <span
            className={`shrink-0 text-2xl text-(--color-acento) transition-transform
              ${abiertos ? 'rotate-180' : ''}`}
            aria-hidden
          >
            ▾
          </span>
        </button>

        {abiertos && (
          <>
            {delDia.length === 0 ? (
              <Vacio>Todavía no hay partidos. El primero que carguen aparece acá.</Vacio>
            ) : (
              delDia.map((partido) => (
                <Tarjeta key={partido.id} compacta>
                  <MarcadorPartido
                    partido={partido}
                    yoId={yo.jugador.id}
                    accion={yo.jugador.rol === 'admin' && accionDeAdmin(partido)}
                    onAbrir={() => onVerPartido(partido.id)}
                  />

                  {/* Anular es lo unico que arregla un partido ya confirmado: no
                      se borra, queda tachado en el historial con quien lo anulo y
                      por que, y el Elo de los cuatro se recalcula solo. */}
                  {porAnular === partido.id && (
                    <div className="mt-2 flex flex-col gap-2 border-t border-(--color-borde) pt-2">
                      <span className="text-sm">¿Por qué lo anulás?</span>
                      <div className="flex flex-wrap gap-2">
                        <Boton
                          chico
                          tipo="peligro"
                          onClick={() => anular(partido.id, 'duplicado')}
                          deshabilitado={ocupado === partido.id}
                        >
                          Estaba duplicado
                        </Boton>
                        <Boton
                          chico
                          tipo="peligro"
                          onClick={() => anular(partido.id, 'otro')}
                          deshabilitado={ocupado === partido.id}
                        >
                          Otro motivo
                        </Boton>
                        <Boton chico tipo="secundario" onClick={() => setPorAnular(null)}>
                          Volver
                        </Boton>
                      </div>
                    </div>
                  )}
                </Tarjeta>
              ))
            )}
          </>
        )}

        {/* La placa, a la vista aunque la tarjeta este plegada. */}
        {noche && viendoPlaca && <PlacaNoche fecha={noche} onCerrar={() => setViendoPlaca(false)} />}
        {noche && !viendoPlaca && (
          <button
            onClick={() => setViendoPlaca(true)}
            className="rounded-xl border border-(--color-acento) bg-(--color-panel) py-2.5 text-center
              font-semibold transition active:scale-[0.98]"
          >
            📸 Placa de la noche · {cuando(noche)}
          </button>
        )}

        {(partidos.confirmados.length > 0 || partidos.pendientes.length > 0) && (
          <button
            onClick={onVerHistorial}
            className="rounded-xl bg-(--color-acento) py-2.5 text-center font-semibold
              text-(--color-sobre-acento) transition active:scale-[0.98]"
          >
            Ver todos los partidos →
          </button>
        )}
      </div>
    </div>
  );
}

/** "hoy", "ayer" o "el 20/9": cuando fue la ultima noche. */
function cuando(fecha: string): string {
  const dias = diasEntre(fecha, hoy());
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  const [, m, d] = fecha.split('-').map(Number);
  return `el ${d}/${m}`;
}

/**
 * Quien tiene que confirmar, con nombre. "Esperando al rival" no le decia a
 * nadie que el que tenia que tocar el boton era el.
 */
function quienFalta(partido: Partido, yoId: number): string {
  const equipoDelQueCargo = partido.jugadores.find((j) => j.id === partido.cargado_por)?.equipo;
  // Lo cargo un admin que no jugo: no hay rival que pueda confirmarlo.
  if (equipoDelQueCargo === undefined) return 'Lo confirma un admin, o cuenta solo pasado el plazo.';

  const rivales = partido.jugadores
    .filter((j) => j.equipo !== equipoDelQueCargo)
    .map((j) => j.nombre)
    .join(' o ');
  return partido.cargado_por === yoId
    ? `Avisale a ${rivales} que lo confirmen desde la app.`
    : `Falta que lo confirme ${rivales}.`;
}
